/**
 * MangelDialog - Registrer eller rediger en mangel
 * For strukturert mangelhåndtering i boligtvister
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './MangelDialog.css';

export default function MangelDialog({ sakId, mangel, onClose }) {
  const [loading, setLoading] = useState(false);

  const [mangelData, setMangelData] = useState({
    bygningsdel: '',
    fysisk_plassering: '',
    rom: '',
    mangelbeskrivelse: '',
    detaljer: {
      symptomer: '',
      årsak: '',
      konsekvenser: '',
      utbedringskostnad_estimat: ''
    },
    alvorlighetsgrad: 'middels',
    estimert_kostnad: '',
    ansvarlig_part: '',
    status: 'identifisert'
  });

  const alvorlighetsgrader = [
    { value: 'kritisk', label: 'Kritisk', emoji: '🔴', beskrivelse: 'Umiddelbar fare eller store skader' },
    { value: 'alvorlig', label: 'Alvorlig', emoji: '🟠', beskrivelse: 'Betydelige mangler som må utbedres' },
    { value: 'middels', label: 'Middels', emoji: '🟡', beskrivelse: 'Mangler som bør utbedres' },
    { value: 'mindre', label: 'Mindre', emoji: '🟢', beskrivelse: 'Mindre mangler/kosmetiske feil' }
  ];

  const statuser = [
    { value: 'identifisert', label: 'Identifisert' },
    { value: 'dokumentert', label: 'Dokumentert' },
    { value: 'reklamert', label: 'Reklamert' },
    { value: 'akseptert', label: 'Akseptert' },
    { value: 'bestridt', label: 'Bestridt' },
    { value: 'utbedret', label: 'Utbedret' },
    { value: 'kompensert', label: 'Kompensert' },
    { value: 'avvist', label: 'Avvist' }
  ];

  const bygningsdeler = [
    'Bad',
    'Kjøkken',
    'Stue',
    'Soverom',
    'Gang',
    'Garasje',
    'Fasade',
    'Tak',
    'Vindu',
    'Dør',
    'Gulv',
    'Vegg',
    'Himling',
    'Inngang',
    'Trappeoppgang',
    'Fellesarealer',
    'Våtrom',
    'Balkong',
    'Terrasse',
    'Annet'
  ];

  useEffect(() => {
    // Hvis vi redigerer eksisterende mangel, fyll inn data
    if (mangel) {
      setMangelData({
        bygningsdel: mangel.bygningsdel || '',
        fysisk_plassering: mangel.fysisk_plassering || '',
        rom: mangel.rom || '',
        mangelbeskrivelse: mangel.mangelbeskrivelse || '',
        detaljer: mangel.detaljer || {
          symptomer: '',
          årsak: '',
          konsekvenser: '',
          utbedringskostnad_estimat: ''
        },
        alvorlighetsgrad: mangel.alvorlighetsgrad || 'middels',
        estimert_kostnad: mangel.estimert_kostnad || '',
        ansvarlig_part: mangel.ansvarlig_part || '',
        status: mangel.status || 'identifisert'
      });
    }
  }, [mangel]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validering
    if (!mangelData.bygningsdel || !mangelData.mangelbeskrivelse) {
      alert('Bygningsdel og mangelbeskrivelse er påkrevd');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        sak_id: sakId,
        ...mangelData,
        // Konverter estimert_kostnad til tall
        estimert_kostnad: mangelData.estimert_kostnad ?
          parseFloat(mangelData.estimert_kostnad) : null
      };

      if (mangel) {
        // Oppdater eksisterende mangel
        await crmApi.request(`/mangler/${mangel.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
        alert('✓ Mangel oppdatert');
      } else {
        // Opprett ny mangel
        await crmApi.request('/mangler', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        alert('✓ Mangel opprettet');
      }

      onClose();
    } catch (error) {
      console.error('Feil ved lagring av mangel:', error);
      alert('Kunne ikke lagre mangel: ' + (error.message || 'Ukjent feil'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-innhold" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>{mangel ? '✏️ Rediger mangel' : '➕ Ny mangel'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="mangel-form">
          {/* Bygningsdel */}
          <div className="form-gruppe">
            <label htmlFor="bygningsdel">
              Bygningsdel <span className="required">*</span>
            </label>
            <select
              id="bygningsdel"
              value={mangelData.bygningsdel}
              onChange={(e) => setMangelData({ ...mangelData, bygningsdel: e.target.value })}
              required
            >
              <option value="">Velg bygningsdel...</option>
              {bygningsdeler.map(del => (
                <option key={del} value={del}>{del}</option>
              ))}
            </select>
          </div>

          {/* Rom */}
          <div className="form-gruppe">
            <label htmlFor="rom">Rom/område</label>
            <input
              type="text"
              id="rom"
              placeholder="F.eks. 'Hovedbad', 'Soverom 1', 'Garasje'"
              value={mangelData.rom}
              onChange={(e) => setMangelData({ ...mangelData, rom: e.target.value })}
            />
          </div>

          {/* Fysisk plassering */}
          <div className="form-gruppe">
            <label htmlFor="fysisk_plassering">Fysisk plassering</label>
            <input
              type="text"
              id="fysisk_plassering"
              placeholder="F.eks. '2. etasje, nordvegg, ved vindu'"
              value={mangelData.fysisk_plassering}
              onChange={(e) => setMangelData({ ...mangelData, fysisk_plassering: e.target.value })}
            />
          </div>

          {/* Mangelbeskrivelse */}
          <div className="form-gruppe">
            <label htmlFor="mangelbeskrivelse">
              Mangelbeskrivelse <span className="required">*</span>
            </label>
            <textarea
              id="mangelbeskrivelse"
              rows="4"
              placeholder="Beskriv mangelen detaljert..."
              value={mangelData.mangelbeskrivelse}
              onChange={(e) => setMangelData({ ...mangelData, mangelbeskrivelse: e.target.value })}
              required
            />
          </div>

          {/* Ytterligere detaljer (ekspanderbar) */}
          <details className="form-gruppe-ekspanderbar">
            <summary>Ytterligere detaljer (valgfritt)</summary>

            <div className="form-gruppe">
              <label htmlFor="symptomer">Symptomer</label>
              <textarea
                id="symptomer"
                rows="2"
                placeholder="Hva ser/opplever du?"
                value={mangelData.detaljer.symptomer}
                onChange={(e) => setMangelData({
                  ...mangelData,
                  detaljer: { ...mangelData.detaljer, symptomer: e.target.value }
                })}
              />
            </div>

            <div className="form-gruppe">
              <label htmlFor="årsak">Antatt årsak</label>
              <textarea
                id="årsak"
                rows="2"
                placeholder="Hva tror du er årsaken?"
                value={mangelData.detaljer.årsak}
                onChange={(e) => setMangelData({
                  ...mangelData,
                  detaljer: { ...mangelData.detaljer, årsak: e.target.value }
                })}
              />
            </div>

            <div className="form-gruppe">
              <label htmlFor="konsekvenser">Konsekvenser</label>
              <textarea
                id="konsekvenser"
                rows="2"
                placeholder="Hvilke konsekvenser har mangelen?"
                value={mangelData.detaljer.konsekvenser}
                onChange={(e) => setMangelData({
                  ...mangelData,
                  detaljer: { ...mangelData.detaljer, konsekvenser: e.target.value }
                })}
              />
            </div>
          </details>

          {/* Alvorlighetsgrad */}
          <div className="form-gruppe">
            <label>Alvorlighetsgrad</label>
            <div className="radio-gruppe">
              {alvorlighetsgrader.map(grad => (
                <label key={grad.value} className="radio-label">
                  <input
                    type="radio"
                    name="alvorlighetsgrad"
                    value={grad.value}
                    checked={mangelData.alvorlighetsgrad === grad.value}
                    onChange={(e) => setMangelData({ ...mangelData, alvorlighetsgrad: e.target.value })}
                  />
                  <span className="radio-innhold">
                    <span className="radio-emoji">{grad.emoji}</span>
                    <span>
                      <strong>{grad.label}</strong>
                      <br />
                      <small>{grad.beskrivelse}</small>
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Estimert kostnad */}
          <div className="form-gruppe">
            <label htmlFor="estimert_kostnad">Estimert utbedringskostnad (NOK)</label>
            <input
              type="number"
              id="estimert_kostnad"
              placeholder="F.eks. 50000"
              value={mangelData.estimert_kostnad}
              onChange={(e) => setMangelData({ ...mangelData, estimert_kostnad: e.target.value })}
              min="0"
              step="1000"
            />
          </div>

          {/* Ansvarlig part */}
          <div className="form-gruppe">
            <label htmlFor="ansvarlig_part">Ansvarlig part</label>
            <input
              type="text"
              id="ansvarlig_part"
              placeholder="F.eks. 'Selger', 'Entreprenør', 'Takstmann'"
              value={mangelData.ansvarlig_part}
              onChange={(e) => setMangelData({ ...mangelData, ansvarlig_part: e.target.value })}
            />
          </div>

          {/* Status */}
          <div className="form-gruppe">
            <label htmlFor="status">Status</label>
            <select
              id="status"
              value={mangelData.status}
              onChange={(e) => setMangelData({ ...mangelData, status: e.target.value })}
            >
              {statuser.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>

          {/* Handlinger */}
          <div className="dialog-handlinger">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Avbryt
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Lagrer...' : (mangel ? 'Oppdater' : 'Opprett')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
